import { useCallback } from "react"
import type { MapRef } from "react-map-gl/mapbox"
import { centerOfMass } from "@turf/turf"
import { getIGNParcelFromCoordinates, searchParcelNumber, getAddressDetails } from "@/lib/explore/api/geo"
import type { IParcel, RecentParcel } from "@/types/explore"

interface UseParcelSelectionParams {
  mapRef: React.RefObject<MapRef | null>
  setSelectedParcels: React.Dispatch<React.SetStateAction<IParcel[]>>
  setLoading: (key: string, active: boolean) => void
  parcelHoverMarker: { remove: () => void }
  clearSearch: () => void
}

export const useParcelSelection = ({
  mapRef,
  setSelectedParcels,
  setLoading,
  parcelHoverMarker,
  clearSearch,
}: UseParcelSelectionParams) => {
  const handleSelectRecent = useCallback(
    async (rp: RecentParcel) => {
      setLoading("parcel", true)
      try {
        let parcelle: IParcel | null = null

        // Try coordinate-based lookup first (most reliable)
        if (rp.center) {
          parcelle = (await getIGNParcelFromCoordinates(rp.center)) ?? null
        }

        // Fallback: search by idu components
        if (!parcelle && rp.idu) {
          const codeInsee = rp.idu.substring(0, 5)
          const section = rp.idu.substring(8, 10)
          const numero = rp.idu.substring(10, 14)
          const results = await searchParcelNumber(codeInsee, section, numero)
          parcelle = results?.[0] ?? null
        }

        if (parcelle) {
          const parcelCenter = centerOfMass(parcelle.geometry).geometry.coordinates as [number, number]
          const parcelleId = parcelle.properties?.idu
          mapRef.current?.flyTo({
            center: parcelCenter,
            zoom: 18,
            duration: 1500,
          })
          setSelectedParcels([{ ...parcelle, center: parcelCenter }])

          // Fetch address asynchronously (non-blocking)
          getAddressDetails(parcelCenter[0], parcelCenter[1])
            .then((addrDetails) => {
              const fullAddress = addrDetails.label || undefined
              if (fullAddress) {
                setSelectedParcels((prev) =>
                  prev.map((p) => (p.properties?.idu === parcelleId ? { ...p, fullAddress } : p)),
                )
              }
            })
            .catch(() => {})
        }
      } catch (err) {
        console.error("Error loading recent parcel:", err)
      } finally {
        setLoading("parcel", false)
      }
    },
    [setLoading, mapRef, setSelectedParcels],
  )

  const handleSelectParcelResult = useCallback(
    (parcel: IParcel) => {
      parcelHoverMarker.remove()
      setLoading("parcel", true)
      const parcelCenter = parcel.center || (centerOfMass(parcel.geometry).geometry.coordinates as [number, number])
      mapRef.current?.flyTo({ center: parcelCenter, zoom: 18, duration: 1500 })
      setSelectedParcels([{ ...parcel, center: parcelCenter }])
      clearSearch()

      // Fetch address asynchronously (non-blocking)
      const parcelId = parcel.properties?.idu
      getAddressDetails(parcelCenter[0], parcelCenter[1])
        .then((addrDetails) => {
          const fullAddress = addrDetails.label || undefined
          if (fullAddress) {
            setSelectedParcels((prev) => prev.map((p) => (p.properties?.idu === parcelId ? { ...p, fullAddress } : p)))
          }
        })
        .catch(() => {})
    },
    [clearSearch, parcelHoverMarker, setLoading, mapRef, setSelectedParcels],
  )

  const handleSelectFilterResult = useCallback(
    (parcel: IParcel) => {
      setLoading("parcel", true)
      const parcelCenter = centerOfMass(parcel.geometry).geometry.coordinates as [number, number]
      mapRef.current?.flyTo({
        center: parcelCenter,
        zoom: 16.5,
        duration: 1500,
      })
      setSelectedParcels([{ ...parcel, center: parcelCenter }])

      // Fetch address asynchronously (non-blocking)
      const parcelId = parcel.properties?.idu
      getAddressDetails(parcelCenter[0], parcelCenter[1])
        .then((addrDetails) => {
          const fullAddress = addrDetails.label || undefined
          if (fullAddress) {
            setSelectedParcels((prev) => prev.map((p) => (p.properties?.idu === parcelId ? { ...p, fullAddress } : p)))
          }
        })
        .catch(() => {})
    },
    [setLoading, mapRef, setSelectedParcels],
  )

  return {
    handleSelectRecent,
    handleSelectParcelResult,
    handleSelectFilterResult,
  }
}
